export type ESignatureStatus =
  | "DRAFT"
  | "SENT"
  | "DELIVERED"
  | "VIEWED"
  | "SIGNED"
  | "PARTIAL"
  | "COMPLETED"
  | "DECLINED"
  | "BOUNCED"
  | "BLOCKED"
  | "REVOKED"
  | "ERROR";

export type RecipientRole = "TAXPAYER" | "SPOUSE" | "BUSINESS" | "PRACTITIONER";

export interface ESignatureRecipient {
  id: string;
  role: RecipientRole;
  roleDisplay: string;
  name: string;
  email: string;
  status: ESignatureStatus;
  statusDisplay: string;
  order: number;
  viewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  bounceReason: string;
  createdAt: string;
}

export interface ESignatureEvent {
  id: string;
  eventType: string;
  eventTypeDisplay: string;
  recipient: string | null;
  actor: number | null;
  actorName: string;
  isSystem: boolean;
  detail: string;
  createdAt: string;
}

export interface ESignatureRequest {
  id: string;
  client: string;
  clientName: string;
  case: string | null;
  document: string;
  documentName: string;
  signedDocument: string | null;
  sentBy: number | null;
  sentByName: string;
  status: ESignatureStatus;
  statusDisplay: string;
  signByDate: string | null;
  recipients: ESignatureRecipient[];
  recipientCount: number;
  sentAt: string | null;
  completedAt: string | null;
  voidedAt: string | null;
  createdAt: string;
}

export interface ESignatureRequestDetail extends ESignatureRequest {
  emailSubject: string;
  emailBody: string;
  ccEmails: string[];
  bccEmails: string[];
  provider: string;
  events: ESignatureEvent[];
}

export interface RecipientInput {
  role: RecipientRole;
  name?: string;
  email?: string;
}

export interface SendESignPayload {
  document: string;
  case?: string | null;
  recipients: RecipientInput[];
  email_subject: string;
  email_body: string;
  sign_by_date?: string | null;
  cc?: string[];
  bcc_me?: boolean;
  more_bcc?: string[];
}

export interface SummaryGroup {
  client: string;
  clientName: string;
  pendingCount: number;
  oldestPending: string;
  newCount: number;
}

export interface ESignatureSummary {
  groups: SummaryGroup[];
  totalPending: number;
  totalNew: number;
}

export interface ReportRow {
  id: string;
  createdAt: string;
  requestor: string;
  client: string;
  recipient: string;
  document: string;
  status: string;
}

export interface UsageReport {
  rows: ReportRow[];
  remaining: number;
  quota: number;
}

/** Status → badge tone (semantic tokens only, per CLAUDE.md). */
export const STATUS_TONE: Record<ESignatureStatus, string> = {
  DRAFT: "bg-muted text-muted-foreground",
  SENT: "bg-primary/10 text-primary",
  DELIVERED: "bg-primary/10 text-primary",
  VIEWED: "bg-primary/15 text-primary",
  SIGNED: "bg-success/15 text-success",
  PARTIAL: "bg-warning/15 text-warning",
  COMPLETED: "bg-success/15 text-success",
  DECLINED: "bg-destructive/15 text-destructive",
  BOUNCED: "bg-destructive/15 text-destructive",
  BLOCKED: "bg-destructive/15 text-destructive",
  REVOKED: "bg-muted text-muted-foreground",
  ERROR: "bg-destructive/15 text-destructive",
};

/** Statuses whose requests still support Resend/Revoke actions. */
export const ACTIONABLE_STATUSES: ESignatureStatus[] = [
  "SENT",
  "DELIVERED",
  "VIEWED",
  "BOUNCED",
  "BLOCKED",
];
